import React, { memo } from "react";

import { SearchTexts, SearchResultsTypes } from "../../../utils/constants";
import { useRouter } from "next/router";

import { sanitize } from "@lululemon/ecom-common/src/lib/util";
// styles
import styles from "./search-results.module.scss";

interface SearchMetaData {
  searchResultType: SearchResultsTypes;
  originalSearchTerm: string;
  correctedSearchTerm: string;
}

interface Props {
  searchMetaData: SearchMetaData;
  totalCount: number;
  visibleCount: number;
}

/**
 * Component to display the description of search results based on various conditions.
 *
 * @param {Props} props The props passed to the component.
 */
const SearchResultsDescription: React.FC<Props> = memo(
  ({ searchMetaData, totalCount, visibleCount }: Props) => {
    const router = useRouter();

    // When the page is loading, the searchMetaData from API resposne is not available and the search term from URL is used.
    // This change make search term is displayed to the user even when the searchMetaData is not available.
    const searchTermFromURL = sanitize(router.query?.Ntt);
    const isLoading = !searchMetaData?.searchResultType;

    const countDisplayStr =
      visibleCount && totalCount ? `${visibleCount} of ${totalCount}` : "";

    if (isLoading) {
      return (
        <SearchDescription
          description={`Showing ${countDisplayStr} results for:`}
          searchTerm={searchTermFromURL}
        />
      );
    }

    switch (searchMetaData.searchResultType) {
      case SearchResultsTypes.SEARCH_RESULTS_EXACT_MATCH:
      case SearchResultsTypes.SEARCH_RESULTS_DEFAULT:
        return (
          <SearchDescription
            description={`Showing ${countDisplayStr} results for:`}
            searchTerm={searchMetaData.originalSearchTerm}
          />
        );
      case SearchResultsTypes.SEARCH_RESULTS_MODIFIED_TERM:
        return (
          <SearchDescription
            description={`${SearchTexts.SEARCH_TITLE_MODIFIED_ORIG}'${searchMetaData.originalSearchTerm}', ${SearchTexts.SEARCH_TITLE_MODIFIED_NEW}`}
            searchTerm={searchMetaData.correctedSearchTerm}
          />
        );
      case SearchResultsTypes.SEARCH_RESULTS_PARTIAL_MATCH:
      case SearchResultsTypes.SEARCH_RESULTS_PARTIAL_MATCH_LIMITED:
        return (
          <SearchDescription
            description={SearchTexts.SEARCH_TITLE_PARTIAL_MATCH}
            searchTerm={searchMetaData.originalSearchTerm}
          />
        );
      default:
        return null;
    }
  }
);

SearchResultsDescription.displayName = "SearchResultsDescription";

/**
 * Component to render the search results description.
 *
 * @param {string} description Description text to be displayed.
 * @param {string} [searchTerm] Search term related to the description.
 */
const SearchDescription: React.FC<{
  description: string;
  searchTerm: string;
}> = ({ description, searchTerm }) => {
  return (
    <section className={styles.resultsDescription}>
      <p className={`${styles.resultsTitle} lll-text-body-1`}>{description}</p>
      {searchTerm && (
        <p className={`${styles.searchTerm} lll-text-small`}>{searchTerm}</p>
      )}
    </section>
  );
};

export default SearchResultsDescription;
